// HQ coupon codes used by campaigns. A shared code is one code every customer can use.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { Confirm, EmptyState, Loader, Modal, Spinner, WelcomeBar } from '../components/ui';

const KINDS = [
  { id: 'percent', label: 'Percent' },
  { id: 'amount', label: 'Amount' },
  { id: 'price', label: 'Price' },
];

function discountText(row) {
  if (row.discount) return row.discount;
  const kind = row.discount_kind || row.kind;
  const value = row.discount_value ?? row.value;
  if (value == null || value === '') return '—';
  if (kind === 'percent') return `${value}%`;
  if (kind === 'amount') return `₹${value} off`;
  if (kind === 'price') return `₹${value}`;
  return String(value);
}

function CouponForm({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('percent');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.createCouponOffer({
        name: name.trim(),
        discount_kind: kind,
        discount_value: Number(value) || 0,
        code: code.trim(),
        code_type: 'shared',
      });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New coupon offer" onClose={onClose}>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={name} onChange={(e) => setName(e.target.value)} placeholder="Diwali 20% off" />
        </label>
        <label className="de-field">
          <span className="de-label">Kind</span>
          <select className="de-select" value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">Value</span>
          <input className="de-input de-input--bordered" type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Shared code</span>
          <input className="de-input de-input--bordered" value={code} onChange={(e) => setCode(e.target.value)} placeholder="DIWALI20" />
        </label>
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy || !name.trim()} onClick={save}>
          {busy ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

export default function CouponOffers() {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .listCouponOffers()
      .then(setRows)
      .catch((e) => {
        toast.error(e.message);
        setRows((current) => current ?? []);
      });

  useEffect(() => {
    load();
  }, []);

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteCouponOffer(deleting.id);
      toast.success('Deleted');
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="de-fade">
      <WelcomeBar title="Coupon offers" sub="Shared codes campaigns can attach. One code for every customer who receives it." />
      <div className="de-panel">
        <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div className="de-panel-title" style={{ margin: 0 }}>
            Coupon offers
          </div>
          <button type="button" className="de-btn de-btn--primary" onClick={() => setCreating(true)}>
            New coupon offer
          </button>
        </div>
        {!rows ? (
          <Loader />
        ) : rows.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Discount</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="de-td-strong">{row.name || '—'}</td>
                      <td>{row.code || '—'}</td>
                      <td>{discountText(row)}</td>
                      <td className="de-td-right">
                        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDeleting(row)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState icon="🏷" title="No coupon offers yet" body="Add a shared code campaigns can attach." />
        )}
      </div>
      {creating ? (
        <CouponForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      ) : null}
      {deleting ? (
        <Confirm
          title="Delete this coupon offer?"
          message={deleting.name || deleting.code || 'This code'}
          confirmLabel="Delete"
          busy={busy}
          onCancel={() => setDeleting(null)}
          onConfirm={remove}
        />
      ) : null}
    </div>
  );
}
